import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Check } from "lucide-mui";
import { useQuery } from "@tanstack/react-query";
import { keys } from "../../../api/queryKeys.ts";
import { api, type RolePermissionLevel } from "../../../api/client.ts";
import { orgRoleLabel } from "../../../auth/orgRoles.ts";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import PageHeader from "../../../components/PageHeader.tsx";
import Panel from "../../../components/Panel.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { formatNumber, humanize } from "../../../utils/format.ts";

const LEVEL_TITLES: Record<string, string> = {
  global: "Global roles",
  organization: "Organization roles",
};

const LEVEL_BLURBS: Record<string, string> = {
  global:
    "Granted via the user's account-level roles (User > Admin > Developer, each inheriting the previous). Control access to platform features.",
  organization:
    "Granted per organization membership. A user can hold a different role in each organization they belong to.",
};

/** "CRM reader" from the org role list, else the humanized name ("Admin", "Publisher"). */
function roleLabel(name: string): string {
  const label = orgRoleLabel(name);
  return label === name ? humanize(name) : label;
}

function LevelMatrix({ level }: { level: RolePermissionLevel }) {
  return (
    <Panel
      title={LEVEL_TITLES[level.level] ?? humanize(level.level)}
      subtitle={LEVEL_BLURBS[level.level]}
      disablePadding
      sx={{ mb: 3 }}
    >
      <Stack spacing={1} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
        {level.roles.map((role) => (
          <Typography key={role.name} variant="body2">
            <Box component="span" sx={{ fontWeight: 600 }}>
              {roleLabel(role.name)}
            </Box>
            {role.description ? ` — ${role.description}` : null}{" "}
            <Box component="span" sx={{ color: "text.secondary" }}>
              ({formatNumber(role.scopes.length)} permissions)
            </Box>
          </Typography>
        ))}
      </Stack>

      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell>Permission</TableCell>
            {level.roles.map((role) => (
              <TableCell key={role.name} align="center">
                {roleLabel(role.name)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {level.scopes.map((scope) => (
            <TableRow key={scope.name} hover>
              <TableCell>
                <Tooltip title={scope.description} placement="right">
                  <Box
                    component="code"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      cursor: scope.description ? "help" : "default",
                    }}
                  >
                    {scope.name}
                  </Box>
                </Tooltip>
                {scope.description && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "text.secondary" }}
                  >
                    {scope.description}
                  </Typography>
                )}
              </TableCell>
              {level.roles.map((role) => (
                <TableCell key={role.name} align="center">
                  {role.scopes.includes(scope.name) ? (
                    <Check fontSize="small" color="success" />
                  ) : (
                    <Box component="span" sx={{ color: "text.disabled" }}>
                      —
                    </Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}

function RolesPermissionsPage() {
  const { data, isLoading } = useQuery({
    queryKey: keys.rolePermission.list(),
    queryFn: api.getRolesPermissions,
  });

  return (
    <>
      <PageHeader
        title="Roles & permissions"
        description={
          <>
            Reference for every role and the permissions (scopes) it grants. Read-only — the
            source of truth lives in the backend auth config
          </>
        }
      />

      {isLoading && <CenteredSpinner />}
      {data?.levels.map((level) => <LevelMatrix key={level.level} level={level} />)}
    </>
  );
}

export default RolesPermissionsPage;
